import { ArrowLeft, CheckCircle, Clock, Mail, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Refund() {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg shadow-lg shadow-violet-500/25">
              📊
            </div>
            <span className="text-xl font-bold text-white">WatchTheSize</span>
          </Link>
          
          <div className="flex items-center gap-6 text-sm text-zinc-400">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/refund" className="text-white font-medium">Refund</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Refund Policy
          </h1>
          
          <p className="text-zinc-400 mb-12">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* Quick Summary */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 mb-12">
            <h2 className="text-xl font-bold text-white mb-4">Quick Summary</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-zinc-300">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong className="text-white">14-day free trial</strong> — no credit card required, cancel anytime</span>
              </li>
              <li className="flex items-start gap-3 text-zinc-300">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong className="text-white">30-day money-back guarantee</strong> for all new subscriptions</span>
              </li>
              <li className="flex items-start gap-3 text-zinc-300">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong className="text-white">No questions asked</strong> — we process refunds promptly</span>
              </li>
            </ul>
          </div>

          <div className="prose prose-invert prose-zinc max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">1. Our Refund Philosophy</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                At WatchTheSize, we want you to be completely satisfied with our service. We offer a generous refund policy because we believe in the value we provide. If our service doesn't meet your expectations, we'll make it right.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                We understand that sometimes a product just isn't the right fit, and we don't want you to feel stuck with something that doesn't work for you.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">2. Free Trial</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                All paid plans include a 14-day free trial:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>No credit card required to start</li>
                <li>Full access to all features during the trial</li>
                <li>Cancel anytime during the trial with no charge</li>
                <li>Automatic reminder before trial ends</li>
                <li>If you don't upgrade, you'll be moved to the Free plan</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">3. 30-Day Money-Back Guarantee</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                If you're not satisfied with your paid subscription within the first 30 days of purchase, you're eligible for a full refund. This applies to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>First-time subscribers to any paid plan</li>
                <li>Upgrades from Free to a paid plan</li>
                <li>Upgrades to a higher-tier plan (prorated for the difference)</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                The 30-day period starts from the date of your first payment, not from the start of any free trial.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">4. Eligibility for Refunds</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Eligible for Refund:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-6">
                <li>First payment on a new subscription (within 30 days)</li>
                <li>Upgrade to a higher plan (difference, within 30 days of upgrade)</li>
                <li>Service outages exceeding 24 hours in a billing period</li>
                <li>Documented technical issues that we cannot resolve</li>
                <li>Accidental duplicate payments</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Not Eligible for Refund:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Requests made after 30 days from payment</li>
                <li>Recurring subscription payments after the first month</li>
                <li>Account termination due to Terms of Service violations</li>
                <li>Unused portions of annual subscriptions (after 30 days)</li>
                <li>Add-on services purchased separately (after usage)</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">5. How to Request a Refund</h2>
              
              <div className="space-y-6 mt-6">
                <div className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Step 1: Contact Us</h4>
                    <p className="text-sm text-zinc-400">
                      Email us at <span className="text-violet-400">billing@watchthesize.com</span> with your account email and reason for the refund.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Step 2: We Review</h4>
                    <p className="text-sm text-zinc-400">
                      We'll review your request and respond within 1-2 business days. For eligible refunds, we'll confirm the amount.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Step 3: Refund Processed</h4>
                    <p className="text-sm text-zinc-400">
                      Approved refunds are processed within 5-10 business days. The funds will be returned to your original payment method.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">6. Cancellation Policy</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                You can cancel your subscription at any time from your account settings. When you cancel:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>You'll retain access until the end of your current billing period</li>
                <li>No further charges will be made</li>
                <li>Your data will be retained for 30 days after cancellation</li>
                <li>You can reactivate your subscription at any time</li>
                <li>After 30 days, your monitoring data will be deleted</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">7. Prorated Refunds</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                In certain circumstances, we may offer prorated refunds:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>When downgrading from a higher-tier plan</li>
                <li>For extended service outages</li>
                <li>At our discretion for exceptional circumstances</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">8. Annual Subscriptions</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                For annual subscriptions:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Full refund available within 30 days of purchase</li>
                <li>After 30 days, refunds are not available for unused months</li>
                <li>You can cancel anytime, and access continues until the end of the annual period</li>
                <li>No automatic renewal charges after cancellation</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">9. Special Circumstances</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We understand that life happens. If you're facing special circumstances (medical emergency, financial hardship, etc.), please reach out to us. We'll work with you to find a fair solution, which may include:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Extended refund windows</li>
                <li>Temporary account suspension</li>
                <li>Plan downgrades with credits</li>
                <li>Payment plan arrangements</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">10. Disputes</h2>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-300">
                    Please contact us before disputing a charge with your bank or payment provider. We're committed to resolving issues quickly and fairly.
                  </p>
                </div>
              </div>
              <p className="text-zinc-400 leading-relaxed">
                If you dispute a charge without first contacting us, it may result in account suspension and could affect your ability to use our service in the future.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">11. Contact Us</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                For refund requests or billing questions, please contact our billing team:
              </p>
              <div className="space-y-2">
                <p className="text-violet-400">billing@watchthesize.com</p>
              </div>
              <p className="text-zinc-400 leading-relaxed mt-4">
                We typically respond within 1-2 business days. Please include your account email address in all correspondence.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm">
                📊
              </div>
              <span className="text-sm font-semibold text-white">WatchTheSize</span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/refund" className="text-white">Refund</Link>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
            
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} WatchTheSize. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

